import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createFournisseur, updateFournisseur, getFournisseurById } from "../services/fournisseurs";
import "./SalarieForm.css";

const FournisseurForm = () => {
  const [formData, setFormData] = useState({
    name: "",
  });
  const [computedBudget, setComputedBudget] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) {
      loadFournisseur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadFournisseur = async () => {
    try {
      const fournisseur = await getFournisseurById(id);
      setFormData({
        name: fournisseur.name || "",
      });
      setComputedBudget(Number(fournisseur.budget || 0));
    } catch (err) {
      console.error("Error loading fournisseur:", err);
      setError("Erreur lors du chargement du fournisseur");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = {
        name: formData.name.trim(),
      };

      if (!submitData.name) {
        setError("Le nom du fournisseur est requis");
        setLoading(false);
        return;
      }

      if (isEdit) {
        await updateFournisseur(id, submitData);
      } else {
        await createFournisseur(submitData);
      }

      navigate("/fournisseurs");
    } catch (err) {
      console.error("Error saving fournisseur:", err);
      setError(isEdit ? "Erreur lors de la modification" : "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="salarie-form-container">
      {/* Updated styling for perfect centering */}
      <style>
        {`
          .salarie-form-container {
            min-height: 100vh;
            width: 100vw;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            margin: 0;
            position: fixed;
            top: 0;
            left: 0;
            overflow: auto;
          }

          .salarie-form-container::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: 
              radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%);
            z-index: 0;
            animation: backgroundFloat 8s ease-in-out infinite;
          }

          @keyframes backgroundFloat {
            0%, 100% { transform: scale(1) rotate(0deg); }
            50% { transform: scale(1.05) rotate(1deg); }
          }

          .salarie-form-wrapper {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            padding: 3rem 2.5rem;
            box-shadow: 
              0 25px 50px rgba(0, 0, 0, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.5);
            width: 90%;
            max-width: 580px;
            position: relative;
            z-index: 1;
            border: 1px solid rgba(255, 255, 255, 0.3);
            animation: slideInScale 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }

          @keyframes slideInScale {
            0% {
              opacity: 0;
              transform: translateY(50px) scale(0.9);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .salarie-form-header {
            text-align: center;
            margin-bottom: 2.5rem;
            position: relative;
          }

          .salarie-form-title {
            font-size: 2.5rem;
            font-weight: 800;
            background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin: 0 0 0.5rem 0;
            line-height: 1.2;
            text-shadow: 0 2px 4px rgba(0,0,0,0.1);
            position: relative;
          }

          .salarie-form-title::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 60px;
            height: 3px;
            background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.7), transparent);
            border-radius: 2px;
          }

          .salarie-back-btn {
            background: linear-gradient(135deg, #f093fb, #f5576c);
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 12px;
            font-weight: 600;
            font-size: 0.9rem;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            box-shadow: 0 4px 15px rgba(245, 87, 108, 0.3);
            margin-top: 1rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            position: relative;
            overflow: hidden;
          }

          .salarie-back-btn::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
            transition: left 0.6s;
          }

          .salarie-back-btn:hover::before {
            left: 100%;
          }

          .salarie-back-btn:hover {
            transform: translateY(-2px) scale(1.05);
            box-shadow: 0 8px 25px rgba(245, 87, 108, 0.4);
            background: linear-gradient(135deg, #f5576c, #f093fb);
          }

          .salarie-error-message {
            background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
            color: white;
            padding: 1rem 1.5rem;
            border-radius: 12px;
            margin-bottom: 1.5rem;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            font-weight: 500;
            box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.2);
            animation: shake 0.5s ease-in-out;
          }

          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
          }

          .error-icon {
            font-size: 1.2rem;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }

          .salarie-form {
            display: flex;
            flex-direction: column;
            gap: 2rem;
          }

          .salarie-form-grid {
            display: grid;
            gap: 1.5rem;
          }

          .salarie-form-field {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            opacity: 0;
            animation: slideInUp 0.6s ease forwards;
          }

          .salarie-form-field:nth-child(1) {
            animation-delay: 0.2s;
          }

          .salarie-form-field:nth-child(2) {
            animation-delay: 0.4s;
          }

          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .salarie-form-label {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .label-text {
            font-weight: 700;
            color: #2d3748;
            font-size: 0.95rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .label-text::before {
            content: '';
            width: 4px;
            height: 20px;
            background: linear-gradient(135deg, #667eea, #764ba2);
            border-radius: 2px;
            animation: glow 3s ease-in-out infinite;
          }

          @keyframes glow {
            0%, 100% { box-shadow: 0 0 5px rgba(102, 126, 234, 0.3); }
            50% { box-shadow: 0 0 15px rgba(102, 126, 234, 0.6); }
          }

          .salarie-form-input {
            padding: 1rem 1.25rem;
            border: 2px solid #e2e8f0;
            border-radius: 12px;
            font-size: 1rem;
            font-weight: 500;
            color: #2d3748;
            background: white;
            transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
            position: relative;
          }

          .salarie-form-input:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 
              0 0 0 3px rgba(102, 126, 234, 0.1),
              0 4px 12px rgba(0, 0, 0, 0.1);
            transform: translateY(-1px) scale(1.01);
          }

          .salarie-form-input::placeholder {
            color: #a0aec0;
            font-weight: 400;
          }

          .salarie-form-input[type="number"] {
            background: linear-gradient(135deg, #f7fafc, #edf2f7);
          }

          .salarie-form-actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 1rem;
            flex-wrap: wrap;
            opacity: 0;
            animation: slideInUp 0.6s ease forwards;
            animation-delay: 0.6s;
          }

          .salarie-form-btn {
            padding: 1rem 2rem;
            border: none;
            border-radius: 14px;
            font-weight: 700;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            min-width: 140px;
            position: relative;
            overflow: hidden;
          }

          .salarie-form-btn::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            background: rgba(255, 255, 255, 0.2);
            border-radius: 50%;
            transition: all 0.6s ease;
            transform: translate(-50%, -50%);
          }

          .salarie-form-btn:hover::before {
            width: 300px;
            height: 300px;
          }

          .salarie-form-btn.cancel {
            background: linear-gradient(135deg, #718096, #4a5568);
            color: white;
            box-shadow: 0 4px 15px rgba(113, 128, 150, 0.3);
          }

          .salarie-form-btn.cancel:hover:not(:disabled) {
            background: linear-gradient(135deg, #4a5568, #2d3748);
            transform: translateY(-2px) scale(1.05);
            box-shadow: 0 8px 25px rgba(113, 128, 150, 0.4);
          }

          .salarie-form-btn.submit {
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: white;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
            font-size: 1.1rem;
            padding: 1.25rem 2.5rem;
          }

          .salarie-form-btn.submit:hover:not(:disabled) {
            background: linear-gradient(135deg, #764ba2, #667eea);
            transform: translateY(-2px) scale(1.05);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
          }

          .salarie-form-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none !important;
          }

          .loading-spinner {
            width: 20px;
            height: 20px;
            border: 2px solid transparent;
            border-top: 2px solid currentColor;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            display: inline-block;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }

          /* Mobile responsive */
          @media (max-width: 768px) {
            .salarie-form-container {
              align-items: flex-start;
              padding: 2rem 1rem;
            }

            .salarie-form-wrapper {
              padding: 2rem 1.5rem;
              border-radius: 20px;
              width: calc(100% - 2rem);
              max-width: none;
              margin: 0;
            }

            .salarie-form-title {
              font-size: 2rem;
            }

            .salarie-form-actions {
              flex-direction: column;
              gap: 0.75rem;
            }

            .salarie-form-btn {
              width: 100%;
              justify-content: center;
            }

            .salarie-back-btn {
              width: auto;
              justify-content: center;
            }
          }

          /* For very short screens */
          @media (max-height: 600px) {
            .salarie-form-container {
              align-items: flex-start;
              padding: 1rem;
            }
            
            .salarie-form-wrapper {
              margin: 1rem 0;
            }
          }

          /* Enhanced focus states */
          .salarie-form-input:focus + .label-text::before {
            background: linear-gradient(135deg, #f093fb, #f5576c);
            box-shadow: 0 0 8px rgba(240, 147, 251, 0.4);
          }

          /* Success state for valid inputs */
          .salarie-form-input:valid:not(:placeholder-shown) {
            border-color: #48bb78;
            background: linear-gradient(135deg, #f0fff4, #c6f6d5);
          }

          /* Budget field special styling */
          .salarie-form-input[name="budget"] {
            font-family: 'Courier New', monospace;
            font-weight: 600;
            color: #2d5016;
          }

          .salarie-form-input[name="budget"]:focus {
            border-color: #48bb78;
            box-shadow: 
              0 0 0 3px rgba(72, 187, 120, 0.1),
              0 4px 12px rgba(0, 0, 0, 0.1);
          }

          /* Scrollbar styling for container */
          .salarie-form-container::-webkit-scrollbar {
            width: 6px;
          }

          .salarie-form-container::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
          }

          .salarie-form-container::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
          }

          .salarie-form-container::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}
      </style>

      <div className="salarie-form-wrapper">
        <div className="salarie-form-header">
          <h2 className="salarie-form-title">
            {isEdit ? "✏️ Modifier Fournisseur" : "🏢 Ajouter Fournisseur"}
          </h2>
          <button
            type="button"
            onClick={() => navigate("/fournisseurs")}
            className="salarie-back-btn"
          >
            ← Retour à la liste
          </button>
        </div>

        {error && (
          <div className="salarie-error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="salarie-form">
          <div className="salarie-form-grid">
            <div className="salarie-form-field">
              <label className="salarie-form-label">
                <span className="label-text">Nom du fournisseur *</span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="salarie-form-input"
                  placeholder="Ex: Fournisseur ABC"
                  required
                />
              </label>
            </div>

            {isEdit && (
              <div className="salarie-form-field">
                <label className="salarie-form-label">
                  <span className="label-text">Budget (calculé)</span>
                  <input
                    type="text"
                    className="salarie-form-input"
                    value={`${Number(computedBudget || 0).toFixed(2)} €`}
                    readOnly
                    disabled
                  />
                </label>
                <small style={{ color: "#4a5568", marginTop: "0.35rem" }}>
                  Ce montant est la somme des achats liés à ce fournisseur.
                </small>
              </div>
            )}

            {!isEdit && (
              <p style={{ color: "#4a5568", margin: 0, fontSize: "0.95rem" }}>
                Le budget sera calculé automatiquement à partir des achats.
              </p>
            )}
          </div>

          <div className="salarie-form-actions">
            <button
              type="button"
              onClick={() => navigate("/fournisseurs")}
              className="salarie-form-btn cancel"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="salarie-form-btn submit"
              disabled={loading}
            >
              {loading ? (
                <span>
                  <span className="loading-spinner"></span>
                  {isEdit ? "Modification..." : "Création..."}
                </span>
              ) : (
                isEdit ? "💾 Modifier" : " Créer"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FournisseurForm;
